import { memo, useState, useCallback, useEffect } from 'react'
import { programGenresApi } from '../../lib/api.js'
import type { ProgramGenre } from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramGenreMergeModalProps {
  /** Genre absorbé : il disparaît à l'issue de l'opération. */
  source:  ProgramGenre
  /** Cibles possibles, déjà filtrées sur la même chaîne. */
  genres:  ProgramGenre[]
  onMerged: () => void
  onClose:  () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

/**
 * Fusion de deux genres. Irréversible : l'écran nomme le nombre exact de fiches
 * déplacées avant de laisser valider, parce que l'opération touche d'un coup
 * plusieurs centaines de programmes et que rien ne la rejoue en arrière.
 */
export const ProgramGenreMergeModal = memo(function ProgramGenreMergeModal({ source, genres, onMerged, onClose }: ProgramGenreMergeModalProps) {
  const [visible,  setVisible]  = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [targetId, setTargetId] = useState('')
  const [subGenre, setSubGenre] = useState('')

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  const handleClose = () => { setVisible(false); setTimeout(onClose, 200) }

  const programCount = source._count?.programs ?? 0
  const seriesCount  = source._count?.series ?? 0
  const target       = genres.find(g => g.id === targetId)
  const candidates   = genres.filter(g => g.id !== source.id)

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!targetId) return
    setSaving(true); setError(null)
    try {
      await programGenresApi.merge(source.id, { targetId, subGenre: subGenre.trim() || null })
      onMerged()
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }, [source.id, targetId, subGenre, onMerged])

  return (
    <ModalShell title={`Fusionner « ${source.label} »`} size="narrow" visible={visible} onClose={handleClose}>
      <form onSubmit={handleSubmit} className="tp-modal-form">
        {error && (
          <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>
        )}

        <div className="tp-feedback tp-feedback--warning" role="status">
          {programCount} programme{programCount !== 1 ? 's' : ''}
          {seriesCount > 0 && ` et ${seriesCount} série${seriesCount !== 1 ? 's' : ''}`}
          {' '}changeront de genre. « {source.label} » sera supprimé. Cette opération est irréversible.
        </div>

        <div className="tp-form-field">
          <label className="tp-field-label" htmlFor="merge-target">Genre de destination *</label>
          <select
            id="merge-target"
            className="tp-select"
            value={targetId}
            onChange={e => setTargetId(e.target.value)}
            required
            autoFocus
          >
            <option value="">— Choisir un genre —</option>
            {candidates.map(g => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>
        </div>

        {/* Sans ce champ, absorber « Sport / Football » dans « Sport » perdrait
            la précision. Une valeur déjà saisie sur une fiche n'est jamais
            écrasée : elle est plus fine que la règle de fusion. */}
        <div className="tp-form-field">
          <label className="tp-field-label" htmlFor="merge-subgenre">Sous-genre à conserver</label>
          <input
            id="merge-subgenre"
            className="tp-input"
            value={subGenre}
            onChange={e => setSubGenre(e.target.value)}
            placeholder="Ex: Football"
          />
          <p className="tp-field-help">
            Écrit sur les fiches déplacées qui n’ont pas encore de sous-genre. Laissez vide pour n’en
            ajouter aucun.
          </p>
        </div>

        <div className="tp-modal-actions">
          <button type="button" onClick={handleClose} className="tp-button">Annuler</button>
          <button
            type="submit"
            disabled={saving || !targetId}
            className="tp-button tp-button--danger"
            data-state={saving ? 'loading' : undefined}
          >
            {saving ? 'Fusion…' : target ? `Fusionner dans « ${target.label} »` : 'Fusionner'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
})
